import React, { useEffect, useMemo, useState, useRef } from "react";
import "./campaign-form.css";
import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import CampaingsAPI from "../../../api/campaing";

export default function CampaignForm({ onSubmit, initialData, onCancel }) {
    const [form, setForm] = useState({
        title: "",
        description: "",
        meta: "",
        status: "pending",
        active: false,
        countries: [],
    });

    const [countries, setCountries] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(true);
    const [countriesError, setCountriesError] = useState("");
    const [query, setQuery] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [msg, setMsg] = useState("");
    const [openCountries, setOpenCountries] = useState(false);
    const fileInputRef = useRef(null);
    const isEditing = Boolean(initialData?._id || initialData?.id);

    const handleCancel = () => {
        setForm({
            title: "",
            description: "",
            meta: "",
            status: "pending",
            active: false,
            countries: [],
        });
        setImageFile(null);
        setImagePreview("");
        setMsg("");
        if (onCancel) onCancel();
    };

    // Cargar países
    useEffect(() => {
        setLoadingCountries(true);
        CampaingsAPI.getCountries()
            .then((res) => setCountries(res.data || res.data?.data || []))
            .catch(() => setCountriesError("No se pudieron cargar los países."))
            .finally(() => setLoadingCountries(false));
    }, []);

    // Precargar initialData
    useEffect(() => {
        if (!initialData) return;
        const {
            title = "",
            description = "",
            meta = "",
            status = "pending",
            active = false,
            countries: initialCountries = [],
            img,
        } = initialData || {};
        const countryIds = initialCountries
            .map((c) => (typeof c === "string" ? c : c?._id))
            .filter(Boolean);
        setForm({
            title,
            // Si tu backend guardó HTML, lo mostramos tal cual; si era texto plano también funciona.
            description: description ?? "",
            meta: meta ?? "",
            status: status ?? "pending",
            active: !!active,
            countries: countryIds,
        });
        if (img) {
            setImagePreview(img);
            setImageFile(null);
        }
    }, [initialData]);

    // Filtrado
    const filteredCountries = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return countries;
        return countries.filter((c) =>
            `${c.name} ${c.key} ${c.currency}`.toLowerCase().includes(q)
        );
    }, [countries, query]);

    // Handlers básicos
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };
    const handleDescriptionChange = (html) => {
        setForm((p) => ({ ...p, description: html || "" }));
    };
    const toggleCountry = (id) => {
        setForm((p) =>
            p.countries.includes(id)
                ? { ...p, countries: p.countries.filter((x) => x !== id) }
                : { ...p, countries: [...p.countries, id] }
        );
    };
    const clearCountries = () => setForm((p) => ({ ...p, countries: [] }));

    // Imagen
    const handlePickImage = () => fileInputRef.current?.click();
    const handleImageChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) return alert("Solo imágenes.");
        if (f.size > 10 * 1024 * 1024) return alert("Máx 10 MB.");
        setImageFile(f);
        setImagePreview(URL.createObjectURL(f));
    };
    const clearImage = (e) => {
        e?.stopPropagation();
        setImageFile(null);
        setImagePreview("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // DnD imagen
    const onDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) return alert("Solo imágenes.");
        setImageFile(f);
        setImagePreview(URL.createObjectURL(f));
    };
    const onDragOver = (e) => e.preventDefault();

    // Helpers
    const stripHtml = (html) => {
        const tmp = document.createElement("div");
        tmp.innerHTML = html || "";
        return (tmp.textContent || tmp.innerText || "").trim();
    };

    // Submit (soporta async)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setMsg("");

        // Validación manual: título, meta y descripción
        if (!form.title.trim()) {
            setMsg("El título es obligatorio.");
            setSubmitting(false);
            return;
        }
        if (!String(form.meta).trim()) {
            setMsg("La meta es obligatoria.");
            setSubmitting(false);
            return;
        }
        if (stripHtml(form.description) === "") {
            setMsg("La descripción es obligatoria.");
            setSubmitting(false);
            return;
        }

        // (Opcional) Sanitizar HTML antes de enviar/guardar
        const safeDescription = DOMPurify.sanitize(form.description, {
            USE_PROFILES: { html: true },
        });

        const payload = {
            id: initialData?._id || initialData?.id || undefined,
            title: form.title.trim(),
            description: safeDescription, // Enviamos HTML seguro
            meta: Number(form.meta) || 0,
            status: form.status,
            active: !!form.active,
            countries: form.countries,
            img: imageFile || null,
            isEditing,
        };

        try {
            const p = onSubmit?.(payload);
            if (p?.then) await p;
            setMsg(isEditing ? "Guardado." : "Creado.");
        } catch (err) {
            console.error(err);
            setMsg("Error al guardar.");
        } finally {
            setSubmitting(false);
        }
    };

    // Texto botón países (compacto)
    const countriesButtonText = countriesError
        ? "Países (error)"
        : loadingCountries
            ? "Países (…)"
            : form.countries.length === 0
                ? "Países (0)"
                : form.countries.length <= 3
                    ? countries
                        .filter((c) => form.countries.includes(c._id))
                        .map((c) => c.key || c.name)
                        .join(", ")
                    : `Países (${form.countries.length})`;

    // Config Quill
    const quillModules = useMemo(
        () => ({
            toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                ["link", "blockquote", "code-block"],
                ["clean"],
            ],
            clipboard: { matchVisual: false },
        }),
        []
    );
    const quillFormats = useMemo(
        () => [
            "header",
            "bold",
            "italic",
            "underline",
            "strike",
            "list",
            "bullet",
            "align",
            "link",
            "blockquote",
            "code-block",
        ],
        []
    );

    return (
        <form className="x-form" onSubmit={handleSubmit} noValidate>
            {/* Barra superior compacta */}
            <div className="x-bar">
                <h3 className="x-title">
                    {isEditing ? "Editar campaña" : "Nueva campaña"}
                </h3>
                <div className="x-actions">
                    <label className="x-switch">
                        <input
                            type="checkbox"
                            name="active"
                            checked={form.active}
                            onChange={handleChange}
                        />
                        <span>Activa</span>
                    </label>

                    {isEditing && (
                        <button type="button" className="x-btn" onClick={handleCancel}>
                            Cancelar
                        </button>
                    )}

                    <button type="submit" className="x-btn x-primary" disabled={submitting}>
                        {submitting ? "…" : isEditing ? "Guardar" : "Crear"}
                    </button>
                </div>
            </div>

            {/* Grid súper denso */}
            <div className="x-grid">
                <input
                    className="x-input"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="Título *"
                    required
                />

                <input
                    className="x-input"
                    name="meta"
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={form.meta}
                    onChange={handleChange}
                    placeholder="Meta *"
                    required
                />

                <select
                    className="x-input"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    aria-label="Estado"
                >
                    <option value="pending">Pendiente</option>
                    <option value="active">Activa</option>
                    <option value="closed">Cerrada</option>
                </select>

                {/* WYSIWYG en lugar de textarea */}
                <div className="x-col-span-2">
                    <label className="x-label">Descripción *</label>
                    <ReactQuill
                        className="x-editor"
                        theme="snow"
                        value={form.description}
                        onChange={handleDescriptionChange}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Describe la campaña…"
                    />
                    {/* Mensaje accesible para validación manual */}
                    <span className="sr-only">
                        Este campo es obligatorio. Se valida al enviar.
                    </span>
                </div>

                {/* Imagen: botón + miniatura */}
                <div className="x-file">
                    <button type="button" className="x-btn" onClick={handlePickImage}>
                        Imagen
                    </button>
                    {imagePreview && (
                        <div
                            className="x-thumb"
                            onClick={clearImage}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            title="Quitar"
                        >
                            <img src={imagePreview} alt="Preview" />
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                    />
                </div>

                {/* Países: multiselect compacto */}
                <div className="x-multi">
                    <button
                        type="button"
                        className="x-btn"
                        onClick={() => setOpenCountries((v) => !v)}
                    >
                        {countriesButtonText}
                    </button>
                    {openCountries && (
                        <div className="x-pop">
                            <input
                                className="x-input x-input--sm"
                                placeholder="Buscar…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                autoFocus
                            />
                            <div className="x-list">
                                {loadingCountries && <div className="x-empty">Cargando…</div>}
                                {countriesError && <div className="x-empty">{countriesError}</div>}
                                {!loadingCountries &&
                                    !countriesError &&
                                    filteredCountries.length === 0 && (
                                        <div className="x-empty">Sin resultados</div>
                                    )}
                                {!loadingCountries &&
                                    !countriesError &&
                                    filteredCountries.map((c) => {
                                        const checked = form.countries.includes(c._id);
                                        return (
                                            <label key={c._id} className="x-row">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleCountry(c._id)}
                                                />
                                                <span className="x-row-name">{c.name}</span>
                                                <span className="x-row-meta">{c.currency}</span>
                                            </label>
                                        );
                                    })}
                            </div>
                            <div className="x-pop-actions">
                                <button
                                    type="button"
                                    className="x-btn"
                                    onClick={() => setOpenCountries(false)}
                                >
                                    OK
                                </button>
                                <button
                                    type="button"
                                    className="x-btn"
                                    onClick={clearCountries}
                                    disabled={form.countries.length === 0}
                                >
                                    Limpiar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Mensaje de estado en una línea */}
            <div className="x-msg" aria-live="polite">
                {msg}
            </div>
        </form>
    );
}